import classNames from 'classnames';
import Head from 'next/head';
import React from 'react';

import Heading from '@jpp/atoms/Heading';
import { EIconType } from '@jpp/atoms/Icon/utils/types';
import SocialItem from '@jpp/atoms/SocialItem';
import {
  ContentBlock,
  ITextStyles,
  PageHero,
} from '@jpp/organisms/AcfComponents';
import UpcomingEvents from '@jpp/organisms/AcfComponents/components/UpcomingEvents';
import Container from '@jpp/shared/Grid/Container';
import Flex from '@jpp/shared/Grid/Flex';
import Row from '@jpp/shared/Grid/Row';
import Section from '@jpp/shared/Grid/Section';
import WYSIWYG from '@jpp/shared/WYSIWYG';
import {
  EFontFamily,
  EPosition,
  ETextTheme,
  ETheme,
} from 'common/typings/enums';
import { IBarTemplateProps } from 'common/typings/types';

import './Bar.scss';

const ROOT_CLASSNAME = 'Bar';

const Bar: React.FunctionComponent<IBarTemplateProps> = ({ page }) => {
  const { acf_fields, media, featured_image, title, content } = page;
  const acf = acf_fields || page.acf;
  const {
    address,
    bar_intro,
    content_blocks,
    email,
    facebook,
    instagram,
    number: contact_number,
    untappd,
    twitter,
    opening_times,
  } = acf || {};
  const socialArray = [
    {
      url: instagram,
      icon: EIconType.Instagram,
    },
    {
      url: twitter,
      icon: EIconType.Twitter,
    },
    {
      url: facebook,
      icon: EIconType.Facebook,
    },
    {
      url: untappd,
      icon: EIconType.Untappd,
    },
  ];
  const image = featured_image
    ? featured_image
    : ({
        url: media?.large,
        alt: `${title} featured image`,
        title,
      } as Core.IMedia);
  const introTextStyles: ITextStyles = {
    text_align: EPosition.Center,
    heading_font: EFontFamily.Oswald,
    heading_color: ETextTheme.TintOmega,
    text_font: EFontFamily.Oswald,
    text_color: ETextTheme.TintOmega,
  };
  const defaultSocialItemProps = {
    theme: ETheme.Brand,
    className: `${ROOT_CLASSNAME}__social-item`,
  };

  return (
    <>
      {image.url && (
        <Head>
          <meta key="og:image" property="og:image" content={image.url} />
        </Head>
      )}
      <div className={classNames(ROOT_CLASSNAME)}>
        {image && (
          <PageHero position={EPosition.Center} heading={title} image={image} />
        )}

        {bar_intro && (
          <ContentBlock
            className={`${ROOT_CLASSNAME}__bar-intro`}
            cta={false}
            position={EPosition.Center}
            text_styles={introTextStyles}
            theme={ETheme.Delta}
            content={bar_intro}
          />
        )}

        <Section>
          <Container>
            <Row>
              <Flex
                className={`${ROOT_CLASSNAME}__location-wrapper`}
                colLg={3}
                colMd={6}
              >
                {address && (
                  <>
                    <Heading
                      className={`${ROOT_CLASSNAME}__heading`}
                      priority={2}
                      children="Location"
                    />

                    <address
                      className={`${ROOT_CLASSNAME}__address`}
                      dangerouslySetInnerHTML={{ __html: address }}
                    />
                  </>
                )}

                {opening_times && (
                  <>
                    <Heading
                      className={`${ROOT_CLASSNAME}__heading`}
                      priority={2}
                      children="Opening Hours"
                    />

                    {opening_times.map((opening, index) => {
                      const { day, closing_time, opening_time } = opening;

                      return (
                        <p
                          key={`${day} ${index}`}
                          className={`${ROOT_CLASSNAME}__copy`}
                        >
                          {`${day}: ${opening_time}-${closing_time}`}
                        </p>
                      );
                    })}
                  </>
                )}
              </Flex>

              <Flex
                className={`${ROOT_CLASSNAME}__location-wrapper`}
                colLg={3}
                colMd={6}
              >
                <Heading
                  className={`${ROOT_CLASSNAME}__heading`}
                  priority={2}
                  children="Contact"
                />

                {email && <p className={`${ROOT_CLASSNAME}__copy`}>{email}</p>}

                {contact_number && (
                  <p className={`${ROOT_CLASSNAME}__copy`}>{contact_number}</p>
                )}

                {socialArray && (
                  <ul className={`${ROOT_CLASSNAME}__social-list`}>
                    {socialArray.map(({ url, icon }, index) => {
                      if (!url) {
                        return null;
                      }

                      return (
                        <SocialItem
                          {...defaultSocialItemProps}
                          key={index}
                          name={icon}
                          link={url}
                        >
                          {`@${url}`}
                        </SocialItem>
                      );
                    })}
                  </ul>
                )}
              </Flex>

              {content && (
                <Flex className={`${ROOT_CLASSNAME}__about-wrapper`} colLg={6}>
                  <Heading
                    className={`${ROOT_CLASSNAME}__heading`}
                    priority={2}
                    children={`About ${title}`}
                  />

                  <WYSIWYG
                    className={`${ROOT_CLASSNAME}__content`}
                    content={content}
                  />
                </Flex>
              )}
            </Row>
          </Container>
        </Section>

        {content_blocks &&
          content_blocks.map((contentBlock, index) => {
            return (
              <ContentBlock
                {...contentBlock}
                key={index}
                contain_image={true}
                className={`${ROOT_CLASSNAME}__content-block`}
                cta={false}
              />
            );
          })}

        <UpcomingEvents
          className={`${ROOT_CLASSNAME}__events`}
          show_featured_event={false}
        />
      </div>
    </>
  );
};

export default Bar;
